import { useGetQuestionnaire, useSubmitQuestionnaire, useUpdateIndividualQuestion } from '@/modules/vendors/requests';
import { Question, QuestionnaireFormData } from '@/modules/vendors/types';
import { QueryKeys } from '@/shared/constants/QueryKeys';
import { usePagination } from '@/shared/hooks/usePagination';
import { addNotification } from '@/shared/states/notification';
import { UploadedFile } from '@/shared/types/file-upload';
import { Spinner } from '@nextui-org/react';
import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EvidenceDocument } from '../../requests';
import AutoFillWithAIModal from './AutoFillWithAIModal';
import DocumentItem from './DocumentItem';
import QuestionField from './QuestionField';
import QuestionnaireHeader from './QuestionnaireHeader';
import RespondOfflineModal from './RespondOfflineModal';
import { TablePagination } from './TablePagination';

// Types for optimized question visibility management (copied from QuestionsForm)
interface ChildrenMapEntry {
  type: 'free_text' | 'single_choice' | 'multiple_choice';
  children: Record<string, string[]>;
}

interface PreprocessedQuestions {
  allQuestions: Map<string, Question>;
  childrenMap: Map<string, ChildrenMapEntry>;
  topLevelQuestions: string[];
}

/**
 * Preprocesses questions to build efficient lookup structures
 */
function preprocessQuestions(questions: Question[]): PreprocessedQuestions {
  const allQuestions = new Map<string, Question>();
  const childrenMap = new Map<string, ChildrenMapEntry>();
  const topLevelQuestions: string[] = [];

  for (const question of questions) {
    const questionNumber = question.question_number.toString();
    allQuestions.set(questionNumber, question);

    if (!questionNumber.includes('.')) {
      topLevelQuestions.push(questionNumber);
    }
  }

  for (const question of questions) {
    const questionNumber = question.question_number.toString();

    if (!questionNumber.includes('.')) {
      continue;
    }

    const parts = questionNumber.split('.');
    const parentQuestionNumber = parts.slice(0, -1).join('.');
    const childSuffix = parts[parts.length - 1];

    const parentQuestion = allQuestions.get(parentQuestionNumber);
    if (!parentQuestion) {
      continue;
    }

    if (!childrenMap.has(parentQuestionNumber)) {
      childrenMap.set(parentQuestionNumber, {
        type: parentQuestion.question_type,
        children: {}
      });
    }

    const parentEntry = childrenMap.get(parentQuestionNumber)!;
    if (!parentEntry.children[childSuffix]) {
      parentEntry.children[childSuffix] = [];
    }
    parentEntry.children[childSuffix].push(questionNumber);
  }

  return {
    allQuestions,
    childrenMap,
    topLevelQuestions
  };
}

interface MergedFormContentProps {
  // Document props
  evidenceDocuments: EvidenceDocument[];
  documentFiles: Record<string, UploadedFile[]>;
  onDocumentFilesChange: (documentId: string, files: UploadedFile[]) => void;
  onUploadError: (documentId: string, hasError: boolean) => void;
  documentSavingStates: Record<string, boolean>;
  customDeleteHandler?: (signedUrl: string, fileId: string) => Promise<void>;
  uploadingDocuments?: Set<string>;
  onDocumentUploadStart?: (documentId: string) => void;
  onDocumentUploadEnd?: (documentId: string) => void;

  // Question props
  questionnaireUrl: string | null | undefined;
  token: string;
  onSavingStateChange?: (isSaving: boolean) => void;
  onPaginationChange?: (paginationComponent: React.ReactNode) => void;
  questionnaireExcelUrl?: string;
  onProgressChange?: (totalRequired: number, completedRequired: number) => void;
  onFormRemount?: () => void;
  formKey: number;
  isReadOnly?: boolean;
}

const MergedFormContent: React.FC<MergedFormContentProps> = ({
  evidenceDocuments,
  documentFiles,
  onDocumentFilesChange,
  onUploadError,
  documentSavingStates,
  customDeleteHandler,
  uploadingDocuments = new Set(),
  onDocumentUploadStart,
  onDocumentUploadEnd,
  questionnaireUrl,
  token,
  onSavingStateChange,
  onPaginationChange,
  questionnaireExcelUrl,
  onProgressChange,
  onFormRemount,
  formKey,
  isReadOnly = false
}) => {
  const [formData, setFormData] = useState<QuestionnaireFormData>({});
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [changedQuestions, setChangedQuestions] = useState<Set<number>>(new Set());

  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  // Header state
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activelyEditingQuestions, setActivelyEditingQuestions] = useState<Set<number>>(new Set());

  // Modal states
  const [isAutoFillModalOpen, setIsAutoFillModalOpen] = useState(false);
  const [isRespondOfflineModalOpen, setIsRespondOfflineModalOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);

  // Scroll management
  const contentTopRef = useRef<HTMLDivElement | null>(null);
  const questionsContainerRef = useRef<HTMLDivElement | null>(null);

  // Pagination
  const { paginationState, setPaginationState, resetPageNumber } = usePagination(1, 25);
  const { currentPage, rowsPerPage } = paginationState;
  const { handlePageChange, handleRowsPerPageChange } = setPaginationState;

  // Fetch questionnaire data
  const {
    data: questions = [],
    isLoading: isLoadingQuestions,
    error: questionError,
    isError: hasQuestionError
  } = useGetQuestionnaire(questionnaireUrl);

  const queryClient = useQueryClient();

  // Preprocess questions
  const preprocessedData = useMemo(() => {
    if (!questions.length) {
      return {
        allQuestions: new Map<string, Question>(),
        childrenMap: new Map<string, ChildrenMapEntry>(),
        topLevelQuestions: []
      };
    }
    return preprocessQuestions(questions);
  }, [questions]);

  /**
   * Get children that should be visible based on parent's response
   */
  const getVisibleChildren = useCallback((parentQuestionNumber: string, parentResponse: string | string[]): string[] => {
    const childrenEntry = preprocessedData.childrenMap.get(parentQuestionNumber);
    if (!childrenEntry) {
      return [];
    }

    const children: string[] = [];

    if (childrenEntry.type === 'free_text') {
      if (parentResponse && (typeof parentResponse === 'string' && parentResponse.trim() !== '')) {
        const aChildren = childrenEntry.children['a'] || [];
        children.push(...aChildren);
      }
      return children;
    }

    if (childrenEntry.type === 'single_choice' && typeof parentResponse === 'string') {
      const parentQuestion = preprocessedData.allQuestions.get(parentQuestionNumber);
      if (!parentQuestion || !parentQuestion.response_choices) {
        return children;
      }

      const selectedIndex = parentQuestion.response_choices.indexOf(parentResponse);
      if (selectedIndex === -1) {
        return children;
      }

      const suffix = String.fromCharCode('a'.charCodeAt(0) + selectedIndex);
      const suffixChildren = childrenEntry.children[suffix] || [];
      children.push(...suffixChildren);
    }

    if (childrenEntry.type === 'multiple_choice' && Array.isArray(parentResponse)) {
      const parentQuestion = preprocessedData.allQuestions.get(parentQuestionNumber);
      if (!parentQuestion || !parentQuestion.response_choices) {
        return children;
      }

      for (const selectedResponse of parentResponse) {
        const selectedIndex = parentQuestion.response_choices.indexOf(selectedResponse);
        if (selectedIndex !== -1) {
          const suffix = String.fromCharCode('a'.charCodeAt(0) + selectedIndex);
          const suffixChildren = childrenEntry.children[suffix] || [];
          children.push(...suffixChildren);
        }
      }
    }

    return children;
  }, [preprocessedData]);

  /**
   * Recursively build visible questions list
   */
  const buildVisibleQuestionsList = useCallback((questionNumber: string, result: string[] = []): void => {
    result.push(questionNumber);

    const question = preprocessedData.allQuestions.get(questionNumber);
    if (!question) {
      return;
    }

    const response = formData[question.question_number];

    if (response !== undefined && response !== null && response !== '') {
      const children = getVisibleChildren(questionNumber, response);

      for (const childQuestionNumber of children) {
        buildVisibleQuestionsList(childQuestionNumber, result);
      }
    }
  }, [formData, getVisibleChildren, preprocessedData.allQuestions]);

  /**
   * Compute the current list of visible questions based on formData
   */
  const visibleQuestions = useMemo(() => {
    const visible: string[] = [];

    for (const topLevelQuestion of preprocessedData.topLevelQuestions) {
      buildVisibleQuestionsList(topLevelQuestion, visible);
    }

    return visible;
  }, [preprocessedData.topLevelQuestions, buildVisibleQuestionsList]);

  // Get actual Question objects from visible question numbers
  const visibleQuestionObjects = useMemo(() => {
    return visibleQuestions
      .map(qNum => preprocessedData.allQuestions.get(qNum))
      .filter((q): q is Question => q !== undefined);
  }, [visibleQuestions, preprocessedData.allQuestions]);

  // Filter questions based on search and status
  const filteredQuestions = useMemo(() => {
    let filtered = visibleQuestionObjects;

    if (searchQuery.trim()) {
      filtered = filtered.filter(question =>
        question.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
        question.question_number.toString().includes(searchQuery)
      );
    }

    if (statusFilter === 'mandatory') {
      filtered = filtered.filter(question => question.required);
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter(question => {
        const questionNumber = question.question_number;
        const value = formData[questionNumber];

        if (activelyEditingQuestions.has(questionNumber)) {
          return true;
        }

        if (question.question_type === 'multiple_choice') {
          return !Array.isArray(value) || value.length === 0;
        } else {
          return !value || (typeof value === 'string' && value.trim() === '');
        }
      });
    } else if (statusFilter === 'completed') {
      filtered = filtered.filter(question => {
        const questionNumber = question.question_number;
        const value = formData[questionNumber];
        if (question.question_type === 'multiple_choice') {
          return Array.isArray(value) && value.length > 0;
        } else {
          return value && (typeof value === 'string' && value.trim() !== '');
        }
      });
    }

    return filtered;
  }, [visibleQuestionObjects, searchQuery, statusFilter, formData, activelyEditingQuestions]);

  // Filter documents based on search and status
  const filteredDocuments = useMemo(() => {
    return evidenceDocuments.filter(doc => {
      // Always show documents that are currently uploading (bypass all filters)
      if (uploadingDocuments.has(doc.id)) {
        return true;
      }

      // For non-uploading documents, apply search filter
      if (searchQuery.trim()) {
        if (!doc.document_name.toLowerCase().includes(searchQuery.toLowerCase())) {
          return false;
        }
      }

      // Apply status filter for non-uploading documents
      if (statusFilter === 'mandatory') {
        return doc.required;
      } else if (statusFilter === 'pending') {
        const files = documentFiles[doc.id] || [];
        return files.length === 0;
      } else if (statusFilter === 'completed') {
        const files = documentFiles[doc.id] || [];
        return files.length > 0;
      }

      // 'all' filter or no filter - show the document
      return true;
    });
  }, [evidenceDocuments, searchQuery, statusFilter, documentFiles, uploadingDocuments]);

  // Calculate starting number for questions (after documents)
  const documentCount = filteredDocuments.length;
  const hasDocuments = filteredDocuments && filteredDocuments.length > 0;

  const totalQuestions = filteredQuestions.length;

  // Calculate total pages accounting for documents on page 1
  const totalPages = useMemo(() => {
    if (!hasDocuments) {
      return Math.ceil(totalQuestions / rowsPerPage);
    }

    // On page 1, documents take up space, leaving fewer slots for questions
    const questionsOnPage1 = Math.max(0, rowsPerPage - documentCount);

    if (totalQuestions <= questionsOnPage1) {
      // All questions fit on page 1 with documents
      return 1;
    }

    // Calculate remaining questions after page 1
    const remainingQuestions = totalQuestions - questionsOnPage1;

    // 1 page (with documents) + pages for remaining questions
    return 1 + Math.ceil(remainingQuestions / rowsPerPage);
  }, [hasDocuments, documentCount, totalQuestions, rowsPerPage]);

  const paginatedQuestions = useMemo(() => {
    if (currentPage === 1) {
      // On page 1, if there are documents, show fewer questions to maintain page size
      const questionsOnPage1 = hasDocuments
        ? Math.max(0, rowsPerPage - documentCount)
        : rowsPerPage;
      return filteredQuestions.slice(0, questionsOnPage1);
    } else {
      // On subsequent pages, adjust start index to account for page 1
      const questionsOnPage1 = hasDocuments
        ? Math.max(0, rowsPerPage - documentCount)
        : rowsPerPage;

      const startIndex = questionsOnPage1 + ((currentPage - 2) * rowsPerPage);
      const endIndex = startIndex + rowsPerPage;

      return filteredQuestions.slice(startIndex, endIndex);
    }
  }, [filteredQuestions, currentPage, rowsPerPage, hasDocuments, documentCount]);

  // Update individual question mutation
  const { mutate: updateQuestion, isPending: isUpdatingQuestion } = useUpdateIndividualQuestion();
  const { mutate: submitQuestionnaire, isPending: isSubmitting } = useSubmitQuestionnaire();

  // Initialize form data when questions are loaded
  const hasInitialized = useRef(false);
  const shouldReinitialize = useRef(false);

  useEffect(() => {
    if (questions.length > 0 && (!hasInitialized.current || shouldReinitialize.current)) {
      const initialData: QuestionnaireFormData = {};
      questions.forEach((question) => {
        const questionNumber = question.question_number;
        if (question.question_type === 'multiple_choice') {
          initialData[questionNumber] = Array.isArray(question.response_value)
            ? question.response_value
            : [];
        } else {
          initialData[questionNumber] = typeof question.response_value === 'string'
            ? question.response_value
            : '';
        }
      });
      setFormData(initialData);
      hasInitialized.current = true;
      shouldReinitialize.current = false;
    }
  }, [questions]);

  // Calculate and report progress
  useEffect(() => {
    if (onProgressChange && visibleQuestionObjects && visibleQuestionObjects.length > 0) {
      const requiredQuestions = visibleQuestionObjects.filter(question => question.required);
      const totalRequired = requiredQuestions.length;

      const completedRequired = requiredQuestions.filter(question => {
        const response = formData[question.question_number];

        if (question.question_type === 'multiple_choice') {
          return Array.isArray(response) && response.length > 0;
        } else {
          return typeof response === 'string' && response.trim().length > 0;
        }
      }).length;

      onProgressChange(totalRequired, completedRequired);
    }
  }, [visibleQuestionObjects, formData, onProgressChange]);

  // Handle form field changes
  const handleFieldChange = useCallback((questionNumber: number, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [questionNumber]: value
    }));

    setActivelyEditingQuestions(prev => new Set(prev).add(questionNumber));

    if (errors[questionNumber]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionNumber];
        return newErrors;
      });
    }

    setChangedQuestions(prev => new Set(prev).add(questionNumber));
    setHasUnsavedChanges(true);
  }, [errors]);

  // Handle comment changes
  const handleCommentChange = useCallback((questionNumber: number, comment: string) => {
    const updatedQuestions = questions.map(q =>
      q.question_number === questionNumber
        ? { ...q, comments: comment }
        : q
    );

    queryClient.setQueryData([QueryKeys.Questionnaire, questionnaireUrl], updatedQuestions);

    setActivelyEditingQuestions(prev => new Set(prev).add(questionNumber));
    setChangedQuestions(prev => new Set(prev).add(questionNumber));
    setHasUnsavedChanges(true);
  }, [questions, queryClient, questionnaireUrl]);

  // Handle immediate checkbox changes
  const handleImmediateFieldChange = useCallback((questionNumber: number, value: string | string[]) => {
    setFormData(prev => ({
      ...prev,
      [questionNumber]: value
    }));

    setActivelyEditingQuestions(prev => new Set(prev).add(questionNumber));

    if (errors[questionNumber]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[questionNumber];
        return newErrors;
      });
    }

    const question = questions.find(q => q.question_number === questionNumber);
    if (question) {
      onSavingStateChange?.(true);

      const updatedQuestion = {
        ...question,
        response_value: value
      };

      updateQuestion(
        {
          questionId: question.id,
          token,
          questionData: updatedQuestion
        },
        {
          onSuccess: () => {
            // Successfully saved
          },
          onError: (error) => {
            addNotification({
              message: `Failed to save answer for question ${questionNumber}. Please try again.`,
              type: 'error'
            });
          },
          onSettled: () => {
            setTimeout(() => {
              onSavingStateChange?.(false);
            }, 2000);
          }
        }
      );
    }
  }, [errors, questions, token, updateQuestion, onSavingStateChange]);

  // Clear actively editing status after period of inactivity
  useEffect(() => {
    if (activelyEditingQuestions.size === 0) return;

    const clearEditingTimer = setTimeout(() => {
      setActivelyEditingQuestions(new Set());
    }, 3000);

    return () => clearTimeout(clearEditingTimer);
  }, [activelyEditingQuestions]);

  // Auto-save functionality
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const autoSaveTimer = setTimeout(() => {
      handleAutoSave();
    }, 500);

    return () => clearTimeout(autoSaveTimer);
  }, [hasUnsavedChanges, changedQuestions.size]);

  const handleAutoSave = useCallback(() => {
    if (!hasUnsavedChanges || changedQuestions.size === 0) return;

    onSavingStateChange?.(true);

    const successfullyUpdated = new Set<number>();
    const initialChangedCount = changedQuestions.size;

    const updatePromises = Array.from(changedQuestions).map(questionNumber => {
      const question = questions.find(q => q.question_number === questionNumber);
      if (!question) return Promise.resolve();

      const updatedQuestion = {
        ...question,
        response_value: formDataRef.current[questionNumber] || (question.question_type === 'multiple_choice' ? [] : '')
      };

      return updateQuestion(
        {
          questionId: question.id,
          token,
          questionData: updatedQuestion
        },
        {
          onSuccess: () => {
            successfullyUpdated.add(questionNumber);
            setChangedQuestions(prev => {
              const newSet = new Set(prev);
              newSet.delete(questionNumber);
              return newSet;
            });
            setActivelyEditingQuestions(prev => {
              const newSet = new Set(prev);
              newSet.delete(questionNumber);
              return newSet;
            });
          },
          onError: (error) => {
            // Keep in changed set for retry
          }
        }
      );
    });

    Promise.allSettled(updatePromises).then(() => {
      if (successfullyUpdated.size === initialChangedCount) {
        setHasUnsavedChanges(false);
      }
      setTimeout(() => {
        onSavingStateChange?.(false);
      }, 2000);
    });
  }, [hasUnsavedChanges, changedQuestions, questions, token, updateQuestion, onSavingStateChange]);

  // Modal handlers
  const handleAutoFill = useCallback(() => {
    setIsAutoFillModalOpen(true);
  }, []);

  const handleRespondOffline = useCallback(() => {
    setIsRespondOfflineModalOpen(true);
  }, []);

  const handleAutoFillConfirm = useCallback(() => {
    setIsGenerating(true);
    setTimeout(() => {
      setIsGenerating(false);
      setIsAutoFillModalOpen(false);
      addNotification({
        message: "Auto-fill completed successfully!",
        type: "success",
      });
    }, 2000);
  }, []);

  const handleRespondOfflineConfirm = useCallback(async (response?: any) => {
    if (response?.success) {
      setIsRespondOfflineModalOpen(false);

      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.Questionnaire, questionnaireUrl]
      });

      await new Promise(resolve => setTimeout(resolve, 500));

      if (onFormRemount) {
        onFormRemount();
      }

      addNotification({
        message: "Offline responses submitted successfully!",
        type: "success",
      });
    } else {
      setIsRespondOfflineModalOpen(false);
    }
  }, [questionnaireUrl, queryClient, onFormRemount]);

  const handleDownloadQuestionnaire = useCallback(() => {
    setIsDownloading(true);
    setTimeout(() => {
      setIsDownloading(false);
      addNotification({
        message: "Questionnaire downloaded successfully!",
        type: "success",
      });
    }, 1000);
  }, []);

  const handleFileUpload = useCallback((files: UploadedFile[]) => {
    setUploadedFiles(files);
  }, []);

  // Track previous filter values
  const prevFilterRef = useRef<{ statusFilter: string; searchQuery: string } | null>(null);

  // Reset pagination when filters change
  useEffect(() => {
    if (prevFilterRef.current === null) {
      prevFilterRef.current = { statusFilter, searchQuery };
      return;
    }

    const filterChanged =
      prevFilterRef.current.statusFilter !== statusFilter ||
      prevFilterRef.current.searchQuery !== searchQuery;

    if (filterChanged && currentPage !== 1) {
      resetPageNumber();
    }

    prevFilterRef.current = { statusFilter, searchQuery };
  }, [statusFilter, searchQuery, currentPage, resetPageNumber]);

  // Scroll to top when page changes
  useEffect(() => {
    // If the scroll happens inside a local container, reset it.
    const container = questionsContainerRef.current;
    if (container && container.scrollTop !== 0) {
      container.scrollTop = 0;
      return;
    }

    // Otherwise, scroll the page to the top of this component.
    // (Common case: window/parent scroll container)
    contentTopRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage]);

  // Pagination component
  const prevPaginationRef = useRef<{
    currentPage: number;
    totalPages: number;
    rowsPerPage: number;
    isDisabled: boolean;
  } | null>(null);

  useEffect(() => {
    const currentPaginationState = {
      currentPage,
      totalPages,
      rowsPerPage,
      isDisabled: isLoadingQuestions || isSubmitting
    };

    const hasPaginationChanged = !prevPaginationRef.current ||
      prevPaginationRef.current.currentPage !== currentPaginationState.currentPage ||
      prevPaginationRef.current.totalPages !== currentPaginationState.totalPages ||
      prevPaginationRef.current.rowsPerPage !== currentPaginationState.rowsPerPage ||
      prevPaginationRef.current.isDisabled !== currentPaginationState.isDisabled;

    if (onPaginationChange && totalPages > 0 && hasPaginationChanged) {
      prevPaginationRef.current = currentPaginationState;
      const paginationComponent = (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          rowsPerPage={rowsPerPage}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
          isDisabled={isLoadingQuestions || isSubmitting}
        />
      );
      onPaginationChange(paginationComponent);
    } else if (onPaginationChange && (totalPages === 0 || !hasPaginationChanged)) {
      if (totalPages === 0) {
        prevPaginationRef.current = null;
        onPaginationChange(null);
      }
    }
  }, [currentPage, totalPages, rowsPerPage, handlePageChange, handleRowsPerPageChange, isLoadingQuestions, isSubmitting, onPaginationChange]);

  // Loading state for questions
  if (isLoadingQuestions || (questionnaireUrl === undefined)) {
    return (
      <div className="flex items-center justify-center h-[calc(70vh)]">
        <div className="text-center">
          <Spinner />
        </div>
      </div>
    );
  }

  // Error state for questions
  if (hasQuestionError || questionnaireUrl === null) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Unable to Load Questionnaire
          </h3>
          <p className="text-sm text-gray-600 mb-2">
            {questionError?.message || (questionnaireUrl === null ? 'No questionnaire URL provided. Please check with your administrator.' : 'Unknown error occurred.')}
          </p>
        </div>
      </div>
    );
  }

  // Check if we have any content to display (use unfiltered counts)
  const hasQuestions = questions.length > 0;
  const hasAnyContent = evidenceDocuments.length > 0 || hasQuestions;

  if (!hasAnyContent) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        <p>No documents or questions available for this assessment.</p>
      </div>
    );
  }

  return (
    <>
      {/* Anchor used to scroll to the top of this content on pagination */}
      <div ref={contentTopRef} />

      {/* Questionnaire Header - for search and filters */}
      <QuestionnaireHeader
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onAutoFill={handleAutoFill}
        onRespondOffline={handleRespondOffline}
        isAutoFillModalOpen={isAutoFillModalOpen}
        setIsAutoFillModalOpen={setIsAutoFillModalOpen}
        isRespondOfflineModalOpen={isRespondOfflineModalOpen}
        setIsRespondOfflineModalOpen={setIsRespondOfflineModalOpen}
        isReadOnly={isReadOnly}
      />

      <div
        ref={questionsContainerRef}
        className="flex flex-col items-start pt-4 gap-4 w-full max-w-[960px] mx-auto px-6"
        data-questions-scroll
      >
        <div className="flex flex-col items-start gap-4 w-full">
          {/* Render Documents First */}
          {hasDocuments && currentPage === 1 && filteredDocuments.map((document, index) => (
            <DocumentItem
              key={document.id}
              itemNumber={index + 1}
              document={document}
              files={documentFiles[document.id] || []}
              onFilesChange={onDocumentFilesChange}
              onUploadError={onUploadError}
              isSaving={documentSavingStates[document.id] || false}
              customDeleteHandler={customDeleteHandler}
              onUploadStart={onDocumentUploadStart}
              onUploadEnd={onDocumentUploadEnd}
              isReadOnly={isReadOnly}
            />
          ))}

          {/* Render Questions After Documents */}
          {hasQuestions && paginatedQuestions.map((question, index) => {
            // Calculate item number accounting for documents and page 1 questions
            let itemNumber: number;
            if (currentPage === 1) {
              // Page 1: document count + question index + 1
              itemNumber = documentCount + index + 1;
            } else {
              // Page 2+: account for questions that were on page 1
              const questionsOnPage1 = hasDocuments
                ? Math.max(0, rowsPerPage - documentCount)
                : rowsPerPage;
              // Total items before this page = documents + page 1 questions + previous pages
              const itemsBeforeThisPage = documentCount + questionsOnPage1 + ((currentPage - 2) * rowsPerPage);
              itemNumber = itemsBeforeThisPage + index + 1;
            }

            return (
              <div
                key={question.question_number}
                className="flex flex-col items-start p-0 gap-1.5 w-full"
              >
                <QuestionField
                  question={question}
                  value={formDataRef.current[question.question_number] || (question.question_type === 'multiple_choice' ? [] : '')}
                  onChange={(value: string | string[]) => handleFieldChange(question.question_number, value)}
                  error={errors[question.question_number]}
                  onCommentChange={(comment: string) => handleCommentChange(question.question_number, comment)}
                  itemNumber={itemNumber}
                  isDisabled={isReadOnly}
                  {...(question.question_type === 'multiple_choice' && !isReadOnly && {
                    enableImmediateApi: true,
                    questionNumber: question.question_number,
                    onImmediateChange: handleImmediateFieldChange
                  })}
                />
              </div>
            );
          })}

          {(filteredQuestions.length === 0 && filteredDocuments.length === 0) && (
            <div className="flex items-center justify-center h-[calc(60vh)] w-full">
              <div className="text-center">
                <p className="text-foreground-400 mb-2">
                  No results found
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Auto-fill Modal */}
      <AutoFillWithAIModal
        isOpen={isAutoFillModalOpen}
        onClose={() => setIsAutoFillModalOpen(false)}
        onConfirm={handleAutoFillConfirm}
        onFileUpload={handleFileUpload}
        isGenerating={isGenerating}
      />

      {/* Respond Offline Modal */}
      <RespondOfflineModal
        isOpen={isRespondOfflineModalOpen}
        onClose={() => setIsRespondOfflineModalOpen(false)}
        onConfirm={handleRespondOfflineConfirm}
        onDownloadQuestionnaire={handleDownloadQuestionnaire}
        isDownloading={isDownloading}
        onFileUpload={handleFileUpload}
        token={token}
        questionnaireExcelUrl={questionnaireExcelUrl}
      />
    </>
  );
};

export default MergedFormContent;
